import type { AppLinkRoute, JobAppLinkRoute } from '../shared/app-links';

/**
 * What a followed capturepoint:// link does in the main window: it raises the same window
 * event the app's own button raises, so a link does exactly what that click does
 * (docs/product/app-links.md). `open` raises none; bringing the window forward is all it
 * asks for, and main has done that. The job links (`job/...`) raise none either: they go
 * to a place in one job, which the window picks (`chooseAppLinkJob`), then open it the way
 * the app's own buttons open it (`jobAppLinkPlaces`).
 *
 * - tour: the event Settings › Appearance › Take the tour raises (`TAKE_TOUR_EVENT` in
 *   DocumentHints.tsx). It runs `startTour`, the function behind every Take a 2-minute tour
 *   button, so a tour under way is resumed rather than started twice. The window raises a link's
 *   event with `detail: { from: "link" }`, so telemetry says the tour was started from a link.
 * - settings/license, settings/performance: the events the license prompts and the Speed
 *   boost status raise to open Settings at License and at Performance.
 */
export const appLinkWindowEvents = {
  tour: 'capturepoint-take-tour',
  'settings/license': 'capturepoint-open-license',
  'settings/performance': 'capturepoint-open-performance',
} as const satisfies Record<Exclude<AppLinkRoute, 'open' | JobAppLinkRoute>, string>;

export const appLinkWindowEvent = (route: AppLinkRoute): string | null =>
  Object.prototype.hasOwnProperty.call(appLinkWindowEvents, route)
    ? appLinkWindowEvents[route as keyof typeof appLinkWindowEvents]
    : null;

/**
 * Where each job link lands, opened the way the app already opens it:
 * - learning: `openLearning(jobId, section)`, the Learning screen on Train or on Results (Settings ›
 *   Training's Train and the speed test's Turn on training open Train the same way).
 * - teach: Learning's Teach in Documents: the job's documents on the Teach tab.
 * - setup: the job door's tiles: Job setup at Lookup lists or at Review thresholds.
 */
export type JobAppLinkPlace =
  | { kind: 'learning'; section: 'results' | 'train' }
  | { kind: 'teach' }
  | { kind: 'setup'; section: 'lookups' | 'thresholds' };

export const jobAppLinkPlaces = {
  'job/learning/results': { kind: 'learning', section: 'results' },
  'job/learning/train': { kind: 'learning', section: 'train' },
  'job/documents/teach': { kind: 'teach' },
  'job/lookups': { kind: 'setup', section: 'lookups' },
  'job/thresholds': { kind: 'setup', section: 'thresholds' },
} as const satisfies Record<JobAppLinkRoute, JobAppLinkPlace>;

/** The place by the names the app shows, for the one line under "Which job?". */
export const jobAppLinkPlaceNames = {
  'job/learning/results': 'Learning › Results',
  'job/learning/train': 'Learning › Train',
  'job/documents/teach': 'Documents',
  'job/lookups': 'Lookup lists',
  'job/thresholds': 'Review thresholds',
} as const satisfies Record<JobAppLinkRoute, string>;

/**
 * Which job a job link opens. A link never names one, so: the person's one job; with
 * several, ask, last used first; with none, the link only opens Paige (as `open`).
 * Sample jobs and the tour's job are not the person's own and never count, even when they
 * are the only jobs (`isSample` says which: onboarding's sample rule and the running tour's
 * job). An archived job is not in use and does not count either. A job still being set up
 * counts; the window opens it as usual rather than at the place (`stillSettingUp`).
 */
export type AppLinkJobChoice =
  { kind: 'none' } | { kind: 'one'; jobId: string } | { kind: 'ask'; jobIds: string[] };

type LinkableJob = { id: string; status: string; updatedAt: string };

export function chooseAppLinkJob<J extends LinkableJob>(
  jobs: readonly J[],
  currentJobId: string | undefined,
  isSample: (job: J) => boolean,
): AppLinkJobChoice {
  const own = jobs.filter((job) => job.status !== 'archived' && !isSample(job));
  if (own.length === 0) return { kind: 'none' };
  if (own.length === 1) return { kind: 'one', jobId: own[0].id };
  // Last used first, as Scan jobs orders them: the job in front, then the most recently used.
  const changed = (job: J) => Date.parse(job.updatedAt) || 0;
  const byRecent = [...own].sort((left, right) => changed(right) - changed(left));
  return {
    kind: 'ask',
    jobIds: [
      ...byRecent.filter((job) => job.id === currentJobId),
      ...byRecent.filter((job) => job.id !== currentJobId),
    ].map((job) => job.id),
  };
}

/** A job still being set up (a proposal, or no document types yet) opens as usual, not at a place. */
export const stillSettingUp = (job: { status: string; documentTypes: readonly unknown[] }) =>
  job.status === 'provisional' || job.documentTypes.length === 0;
